import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { RiFullscreenExitLine, RiFullscreenLine } from "react-icons/ri";
import { countSeens } from "../containers/student/video/func";
import {
  exitDocumentFullscreen,
  exitPlayerFullscreen,
  getFullscreenElement,
  loadVimeoPlayerApi,
  parseVimeoSource,
} from "../helpers/vimeoPlayer";
import { parseVideoQuestion, parseVideoQuizTime } from "../helpers/videoQuiz";
import { videoDataRequest } from "../modules/students-modules/videos/Actions";
import VideoQuickQuiz from "./VideoQuickQuiz";

const randomWatermarkSpot = () => ({
  top: 4 + Math.random() * 68,
  left: 4 + Math.random() * 48,
});

const randomWatermarkHoldMs = () => (1 + Math.floor(Math.random() * 5)) * 1000;

const watermarkText = (value) => (value == null ? "" : String(value).trim());

const playerSrc = (embedUrl) => {
  try {
    const url = new URL(embedUrl);
    url.searchParams.set("pip", "0");
    return url.toString();
  } catch {
    return embedUrl;
  }
};

const VimeoEmbed = ({ videoUrl, data, trackProgress = true }) => {
  const shellRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const lastTimeRef = useRef(0);
  const playedDeltaRef = useRef(0);
  const hasCountedSeenRef = useRef(false);
  const quizShownRef = useRef(false);
  const quizOpenRef = useRef(false);
  const quizTimeRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  const [quizOpen, setQuizOpen] = useState(false);
  const [shellFullscreen, setShellFullscreen] = useState(false);
  const [watermarkSpot, setWatermarkSpot] = useState(randomWatermarkSpot);

  const quizQuestion = parseVideoQuestion(data?.question);
  quizTimeRef.current = quizQuestion ? parseVideoQuizTime(data?.time) : null;
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const watermarkName = watermarkText(userData?.name);
  const watermarkPhone = watermarkText(userData?.phone);
  const showWatermark = Boolean(watermarkName || watermarkPhone);
  const videoData = useSelector((state) => state.chapterVideos.videoData);
  const [searchParams] = useSearchParams();
  const { id: routeId, video_id: routeVideoId } = useParams();

  const parsed = parseVimeoSource(videoUrl);
  const embedUrl = parsed?.embedUrl;
  const embedSrc = embedUrl ? playerSrc(embedUrl) : "";

  useEffect(() => {
    const syncFullscreen = () => {
      setShellFullscreen(getFullscreenElement() === shellRef.current);
    };

    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    };
  }, []);

  const toggleShellFullscreen = () => {
    const shell = shellRef.current;
    if (!shell) return;

    if (getFullscreenElement() === shell) {
      exitDocumentFullscreen();
      return;
    }

    const request = shell.requestFullscreen || shell.webkitRequestFullscreen;
    if (request) Promise.resolve(request.call(shell)).catch(() => {});
  };

  useEffect(() => {
    if (!embedUrl || !showWatermark) return undefined;

    let holdTimer;
    const scheduleMove = () => {
      holdTimer = window.setTimeout(() => {
        setWatermarkSpot(randomWatermarkSpot());
        scheduleMove();
      }, randomWatermarkHoldMs());
    };

    scheduleMove();
    return () => window.clearTimeout(holdTimer);
  }, [embedUrl, showWatermark]);

  useEffect(() => {
    if (!embedUrl || !iframeRef.current || !trackProgress) return;

    let cancelled = false;
    let player;
    quizShownRef.current = false;
    quizOpenRef.current = false;
    setQuizOpen(false);

    const getVideoId = () =>
      dataRef.current?.id ||
      searchParams.get("video") ||
      routeVideoId ||
      routeId;

    const reportProgress = (currentTime, playedDelta) => {
      const videoId = getVideoId();
      if (!videoId || !userData?.id) return;
      if (!currentTime && !playedDelta) return;

      dispatch(
        videoDataRequest({
          total_played: playedDelta || 0,
          total_covered: currentTime || 0,
          video_id: videoId,
          user_id: userData.id,
        })
      );
    };

    const flushProgress = () => {
      const currentTime = lastTimeRef.current;
      const playedDelta = playedDeltaRef.current;
      playedDeltaRef.current = 0;
      reportProgress(currentTime, playedDelta);
    };

    loadVimeoPlayerApi()
      .then((Player) => {
        if (cancelled || !iframeRef.current) return;

        player = new Player(iframeRef.current);
        playerRef.current = player;

        player.on("play", () => {
          if (quizOpenRef.current) {
            player.pause();
            return;
          }
          isPlayingRef.current = true;
          if (!hasCountedSeenRef.current && dataRef.current) {
            hasCountedSeenRef.current = true;
            countSeens(dispatch, dataRef.current);
          }
        });

        player.on("pause", () => {
          isPlayingRef.current = false;
          flushProgress();
        });
        player.on("ended", () => {
          isPlayingRef.current = false;
          flushProgress();
        });

        player.on("timeupdate", (event) => {
          const seconds = event?.seconds || 0;
          if (isPlayingRef.current) {
            const delta = seconds - lastTimeRef.current;
            if (delta > 0 && delta < 2) {
              playedDeltaRef.current += delta;
            }
          }
          lastTimeRef.current = seconds;

          const cue = quizTimeRef.current;
          if (cue != null && !quizShownRef.current && seconds >= cue) {
            quizShownRef.current = true;
            quizOpenRef.current = true;
            isPlayingRef.current = false;
            exitPlayerFullscreen(player).finally(() => {
              player.pause();
              setQuizOpen(true);
            });
          }
        });
      })
      .catch(() => {});

    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        isPlayingRef.current = false;
        flushProgress();
      }
    };

    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("beforeunload", flushProgress);

    return () => {
      cancelled = true;
      isPlayingRef.current = false;
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("beforeunload", flushProgress);
      if (player) {
        player.off("play");
        player.off("pause");
        player.off("ended");
        player.off("timeupdate");
        if (typeof player.destroy === "function") player.destroy();
      }
      playerRef.current = null;
    };
  }, [
    embedUrl,
    trackProgress,
    dispatch,
    userData?.id,
    searchParams,
    routeId,
    routeVideoId,
  ]);

  useEffect(() => {
    if (videoData?.views_count > 3) {
      window.location.reload();
    }
  }, [videoData]);

  const handleContinueQuiz = () => {
    quizOpenRef.current = false;
    setQuizOpen(false);
    playerRef.current?.play();
  };

  if (!parsed) {
    return <div dangerouslySetInnerHTML={{ __html: videoUrl }}></div>;
  }

  return (
    <div
      ref={shellRef}
      style={{
        position: "relative",
        width: "100%",
        height: shellFullscreen ? "100%" : undefined,
        paddingBottom: shellFullscreen ? 0 : "56.25%",
        background: "#000",
      }}
    >
      <iframe
        ref={iframeRef}
        src={embedSrc}
        allow="autoplay"
        title="Vimeo video"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
      {showWatermark && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: `${watermarkSpot.top}%`,
            left: `${watermarkSpot.left}%`,
            zIndex: 2,
            maxWidth: "46%",
            pointerEvents: "none",
            userSelect: "none",
            color: "rgba(255, 255, 255, 0.92)",
            fontSize: "15px",
            fontWeight: 700,
            lineHeight: 1.35,
            textShadow:
              "0 1px 2px rgba(0, 0, 0, 0.9), 0 0 6px rgba(0, 0, 0, 0.65)",
            background: "rgba(0, 0, 0, 0.28)",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {watermarkName}
          {watermarkName && watermarkPhone ? <br /> : null}
          {watermarkPhone}
        </div>
      )}
      <button
        type="button"
        onClick={toggleShellFullscreen}
        aria-label={shellFullscreen ? "الخروج من ملء الشاشة" : "ملء الشاشة"}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          zIndex: 3,
          width: 48,
          height: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          border: 0,
          borderRadius: 4,
          background: "rgba(0, 0, 0, 0.55)",
          color: "#fff",
          cursor: "pointer",
        }}
      >
        {shellFullscreen ? (
          <RiFullscreenExitLine size={20} />
        ) : (
          <RiFullscreenLine size={20} />
        )}
      </button>
      <VideoQuickQuiz
        question={quizQuestion}
        isOpen={quizOpen}
        onContinue={handleContinueQuiz}
      />
    </div>
  );
};

export default VimeoEmbed;
